import { FC, ReactNode } from 'react';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  AppointmentSource,
  AppointmentStatus,
  APPOINTMENT_STATUS_LABELS,
  SOURCE_LABELS,
  UnitOption,
} from './types';
import { unitsSummary } from './unitLabels';

/** Every column the shared row can render. A surface declares which it wants. */
export type AppointmentRowColumn =
  | 'time'
  | 'title'
  | 'detail'
  | 'property'
  | 'units'
  | 'status'
  | 'actions';

/**
 * Fixed tracks. The only fluid column is `title`; everything else holds its
 * width so a long household name, a source chip, or a unit list can never
 * shift the columns after it.
 */
const COLUMN_TRACKS: Record<AppointmentRowColumn, string> = {
  time: '118px',
  title: 'minmax(200px, 1.6fr)',
  detail: 'minmax(160px, 1fr)',
  property: '210px',
  units: '170px',
  status: '92px',
  actions: '360px',
};

/** Registry colours only (DD-026); `scheduled` is deliberately not a chip. */
const STATUS_CHIP_COLOR: Record<
  string,
  'success' | 'warning' | 'error' | 'default'
> = {
  completed: 'success',
  rescheduled: 'warning',
  no_show: 'error',
  cancelled: 'default',
};

const Empty: FC = () => (
  <Typography variant="body2" color="text.disabled">
    —
  </Typography>
);

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export interface AppointmentRowProps {
  /** Surface column contract. Keep it identical for every row on a surface. */
  columns: AppointmentRowColumn[];
  /** Wider time track for surfaces that print the date, not just the clock. */
  timeWidth?: number;
  /** Name the property inside the units cell. Off when the surface already
   * names the property (its own column, or the page itself). */
  unitsShowProperty?: boolean;
  time: string;
  title: string;
  hostName?: string | null;
  detail?: string | null;
  propertyName?: string | null;
  units?: UnitOption[];
  status: AppointmentStatus;
  source?: AppointmentSource;
  /** Fixed-width action cluster; see AppointmentActionBar. */
  actions?: ReactNode;
  /** Amber border for rows awaiting an outcome. */
  attention?: boolean;
  onOpen?: () => void;
}

/**
 * One appointment row, shared by the property worklist, My Calendar, and any
 * future list. It is a CSS grid, not a flex row: the old implementation sized
 * each cell by its content, so the property chip drifted ~480px and the action
 * cluster flipped between one and four lines from row to row.
 */
const AppointmentRow: FC<AppointmentRowProps> = ({
  columns,
  timeWidth = 118,
  unitsShowProperty = false,
  time,
  title,
  hostName,
  detail,
  propertyName,
  units,
  status,
  source,
  actions,
  attention = false,
  onOpen,
}) => {
  const unitsInfo = unitsSummary(units, { showProperty: unitsShowProperty });

  const cell = (column: AppointmentRowColumn): ReactNode => {
    switch (column) {
      case 'time':
        return (
          <Typography
            key={column}
            variant="body2"
            fontWeight={attention ? 600 : 500}
            sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            {time}
          </Typography>
        );
      case 'title':
        return (
          <Stack key={column} spacing={0} sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={attention ? 600 : 500}
              title={title}
              sx={ellipsis}
            >
              {title}
            </Typography>
            {(hostName || (source && source !== 'compliance')) && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                {hostName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={`with ${hostName}`}
                  >
                    with {hostName}
                  </Typography>
                )}
                {source && source !== 'compliance' && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    · {SOURCE_LABELS[source]}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        );
      case 'detail':
        return detail ? (
          <Tooltip key={column} title={detail} placement="top-start">
            <Typography variant="caption" color="text.secondary" sx={ellipsis}>
              {detail}
            </Typography>
          </Tooltip>
        ) : (
          <Empty key={column} />
        );
      case 'property':
        return propertyName ? (
          <Typography
            key={column}
            variant="body2"
            color="text.secondary"
            title={propertyName}
            sx={ellipsis}
          >
            {propertyName}
          </Typography>
        ) : (
          <Empty key={column} />
        );
      case 'units':
        return unitsInfo ? (
          <Tooltip
            key={column}
            title={unitsInfo.tooltip}
            placement="top-start"
            sx={{ whiteSpace: 'pre-line' }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={ellipsis}
              >
                {unitsInfo.text}
              </Typography>
              {unitsInfo.hidden > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`+${unitsInfo.hidden}`}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          </Tooltip>
        ) : (
          <Empty key={column} />
        );
      case 'status':
        return status === 'scheduled' ? (
          <Typography
            key={column}
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {APPOINTMENT_STATUS_LABELS[status]}
          </Typography>
        ) : (
          <Chip
            key={column}
            size="small"
            variant="outlined"
            color={STATUS_CHIP_COLOR[status] ?? 'default'}
            label={
              status === 'no_show'
                ? 'No-show'
                : APPOINTMENT_STATUS_LABELS[status]
            }
            sx={{ justifySelf: 'start' }}
          />
        );
      case 'actions':
        return (
          <Box
            key={column}
            sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}
            onClick={(event) => event.stopPropagation()}
          >
            {actions}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      onClick={onOpen}
      sx={{
        display: 'grid',
        gridTemplateColumns: columns
          .map((c) => (c === 'time' ? `${timeWidth}px` : COLUMN_TRACKS[c]))
          .join(' '),
        alignItems: 'center',
        columnGap: 2,
        minHeight: 64,
        border: 1,
        borderColor: attention ? 'warning.main' : 'divider',
        borderRadius: 1.5,
        px: 2,
        py: 1.25,
        cursor: onOpen ? 'pointer' : 'default',
        '&:hover': onOpen ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      {columns.map((column) => cell(column))}
    </Box>
  );
};

export interface AppointmentActionBarProps {
  onReschedule: () => void;
  onComplete: () => void;
  onNoShow: () => void;
  onCancel: () => void;
  /** Omitted on surfaces where the row click already opens Manage. */
  onManage?: () => void;
}

const actionSx = { px: 0.5, minWidth: 0, whiteSpace: 'nowrap' } as const;

/** Five visible actions, one line, fixed footprint — never a wrapped cluster. */
export const AppointmentActionBar: FC<AppointmentActionBarProps> = ({
  onReschedule,
  onComplete,
  onNoShow,
  onCancel,
  onManage,
}) => (
  <>
    <Button size="small" variant="text" sx={actionSx} onClick={onReschedule}>
      Reschedule
    </Button>
    <Button size="small" variant="text" sx={actionSx} onClick={onComplete}>
      Complete
    </Button>
    <Button
      size="small"
      variant="text"
      color="error"
      sx={actionSx}
      onClick={onNoShow}
    >
      No-show
    </Button>
    <Button
      size="small"
      variant="text"
      color="error"
      sx={actionSx}
      onClick={onCancel}
    >
      Cancel
    </Button>
    {onManage && (
      <Button size="small" variant="text" sx={actionSx} onClick={onManage}>
        Manage
      </Button>
    )}
  </>
);

export default AppointmentRow;
